import { FormEvent, useEffect, useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LEVERAGE_OPTIONS } from '../../constants/accountOptions';
import SourceCombobox from '../common/SourceCombobox';
import PasswordInput from '../common/PasswordInput';
import { mt5API } from '../../services/api';
import { Loader2 } from 'lucide-react';
import {
  preventInvalidNameInput,
  VALIDATION_MESSAGES,
} from '../../utils/validationMessages';

interface CreateDemoAccountFormProps {
  onSubmit?: (data: DemoAccountData) => void;
}

export interface DemoAccountData {
  group: string;
  name: string;
  mainPassword?: string;
  investPassword?: string;
  leverage: string;
}

const CreateDemoAccountForm = ({ onSubmit }: CreateDemoAccountFormProps) => {
  const [formData, setFormData] = useState<DemoAccountData>({
    group: '',
    name: '',
    mainPassword: '',
    investPassword: '',
    leverage: '100',
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof DemoAccountData, string>>
  >({});
  const [groups, setGroups] = useState<string[]>([]);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadGroups = async () => {
      try {
        setIsGroupsLoading(true);
        const response = await mt5API.getGroups();
        const rawData = response?.data || response;
        if (isMounted && Array.isArray(rawData)) {
          const list = rawData.map((item: any) =>
            typeof item === 'string'
              ? item
              : item?.Path || item?.name || item?.group || String(item)
          );
          setGroups(list);
          if (list.length > 0) {
            setFormData(prev => ({ ...prev, group: prev.group || list[0] }));
          }
        }
      } catch (error) {
        console.error('Error fetching MT5 groups:', error);
      } finally {
        if (isMounted) {
          setIsGroupsLoading(false);
        }
      }
    };
    loadGroups();

    return () => {
      isMounted = false;
    };
  }, []);

  const validatePassword = (
    password?: string,
    fieldLabel: string = 'Password'
  ): string | null => {
    if (!password || !password.trim()) {
      return `${fieldLabel} is required`;
    }
    const trimmed = password.trim();
    if (trimmed.length < 8) {
      return `${fieldLabel} must be at least 8 characters long`;
    }
    if (!/[A-Z]/.test(trimmed)) {
      return `${fieldLabel} must contain at least 1 uppercase letter`;
    }
    if (!/[0-9]/.test(trimmed)) {
      return `${fieldLabel} must contain at least 1 number`;
    }
    if (!/[^A-Za-z0-9]/.test(trimmed)) {
      return `${fieldLabel} must contain at least 1 special symbol (e.g. @, #, $, %)`;
    }
    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof DemoAccountData, string>> = {};

    if (!formData.group || !formData.group.trim()) {
      newErrors.group = 'Group is required';
    }

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = VALIDATION_MESSAGES.fullName.required;
    } else if (formData.name.trim().length < 3) {
      newErrors.name = VALIDATION_MESSAGES.fullName.minLength;
    } else if (formData.name.trim().length > 50) {
      newErrors.name = VALIDATION_MESSAGES.fullName.maxLength;
    } else if (!/^[a-zA-Z \-']+$/.test(formData.name.trim())) {
      newErrors.name = VALIDATION_MESSAGES.fullName.pattern;
    }

    const mainPassErr = validatePassword(
      formData.mainPassword,
      'Main password'
    );
    if (mainPassErr) {
      newErrors.mainPassword = mainPassErr;
    }

    const investPassErr = validatePassword(
      formData.investPassword,
      'Investor password'
    );
    if (investPassErr) {
      newErrors.investPassword = investPassErr;
    } else if (
      formData.mainPassword &&
      formData.investPassword?.trim() === formData.mainPassword.trim()
    ) {
      newErrors.investPassword =
        'Investor password must be different from main password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof DemoAccountData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    value: string
  ) => options.find(option => option.value === value)?.label || value;

  const formatLeverageValue = (leverageStr: string): string => {
    if (!leverageStr) return '-';
    let str = String(leverageStr).trim();
    if (str.startsWith('1:')) {
      str = str.substring(2);
    }
    return str || '-';
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      data-drawer-form="true"
      autoComplete="off"
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Group Combobox - Single Row */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="group">Group *</Label>
              {isGroupsLoading && (
                <div className="flex items-center text-xs text-blue-600 gap-1">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Loading groups...</span>
                </div>
              )}
            </div>
            <SourceCombobox
              id="group"
              items={groups}
              placeholder={
                isGroupsLoading ? 'Loading groups...' : 'Select group'
              }
              value={formData.group}
              onChange={value => handleInputChange('group', value)}
              disabled={isGroupsLoading}
              className={errors.group ? 'border-red-500' : ''}
              emptyText="No groups available"
            />
            {errors.group && (
              <p className="text-sm text-red-500">{errors.group}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              name="mt5_demo_name"
              autoComplete="off"
              placeholder="Enter name"
              value={formData.name}
              onKeyDown={preventInvalidNameInput}
              onChange={e => handleInputChange('name', e.target.value)}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <PasswordInput
              id="mainPassword"
              name="mt5_demo_main_password"
              label="Main Password"
              placeholder="Enter main password"
              autoComplete="new-password"
              value={formData.mainPassword}
              onChange={e => handleInputChange('mainPassword', e.target.value)}
              error={errors.mainPassword}
            />
            <PasswordInput
              id="investPassword"
              name="mt5_demo_invest_password"
              label="Investor Password"
              placeholder="Enter investor password"
              autoComplete="new-password"
              value={formData.investPassword}
              onChange={e =>
                handleInputChange('investPassword', e.target.value)
              }
              error={errors.investPassword}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Trading Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="leverage">Leverage</Label>
            <SourceCombobox
              id="leverage"
              items={LEVERAGE_OPTIONS.map(leverage => leverage.label)}
              placeholder="Select leverage"
              value={getOptionLabel(LEVERAGE_OPTIONS, formData.leverage)}
              onChange={value => {
                const leverage = LEVERAGE_OPTIONS.find(
                  option => option.label === value
                );
                if (leverage) {
                  handleInputChange('leverage', leverage.value);
                } else {
                  handleInputChange('leverage', value);
                }
              }}
              emptyText="No leverage options found."
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Account Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Name:</span>
              <span className="text-sm font-medium">
                {formData.name || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Group:</span>
              <span className="text-sm font-medium">
                {formData.group || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Leverage:</span>
              <span className="text-sm font-medium">
                {formatLeverageValue(formData.leverage)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default CreateDemoAccountForm;
